import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import type { RosterProviderCheck } from "@api/shared-runtime/roster-provider-match";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import { CsvFileDropzone } from "./csv-file-dropzone";
import {
  downloadProviderCsvTemplate,
  parseProviderCsv,
  ProviderCsvError,
  type ParsedProviderCsvRow,
} from "./provider-csv-import";
import {
  isRosterNameMatchError,
  parseRosterIdentityConflict,
} from "./roster-match/roster-error-cause";
import {
  getMatchName,
  getMatchStatusLabel,
} from "./roster-match/roster-match-labels";

type ProviderCsvImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (organizationBusinessUnitId: string) => void | Promise<void>;
};

const IDENTITY_CONFLICT_LABEL = "Already on your roster";

const describeMatches = (check: RosterProviderCheck) =>
  check.nameMatches
    .map(
      (match) =>
        `${getMatchName(match)} (${getMatchStatusLabel(match.status)})`,
    )
    .join(", ");

const displayName = (row: ParsedProviderCsvRow) =>
  `${row.values.firstName ?? ""} ${row.values.lastName ?? ""}`.trim() || "—";

const getSelectAllState = ({
  allSelected,
  someSelected,
}: {
  allSelected: boolean;
  someSelected: boolean;
}): boolean | "indeterminate" => {
  if (allSelected) {
    return true;
  }
  if (someSelected) {
    return "indeterminate";
  }
  return false;
};

const getImportSuccessMessage = ({
  providerCount,
  invitationCount,
}: {
  providerCount: number;
  invitationCount: number;
}) => {
  const providerLabel = providerCount === 1 ? "provider" : "providers";
  if (invitationCount === 0) {
    return `${providerCount} ${providerLabel} added.`;
  }
  const invitationLabel = invitationCount === 1 ? "invitation" : "invitations";
  return `${providerCount} ${providerLabel} added; ${invitationCount} ${invitationLabel} sent.`;
};

export function ProviderCsvImportDialog({
  open,
  onOpenChange,
  onSaved,
}: ProviderCsvImportDialogProps) {
  const { selectedOrgBusinessUnit } = useOrganization();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ParsedProviderCsvRow[]>([]);
  const [selectedRowNumbers, setSelectedRowNumbers] = useState<Set<number>>(
    new Set(),
  );
  const [fileError, setFileError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  // Server match results by CSV row number; null until the check finishes.
  const [checks, setChecks] = useState<Map<number, RosterProviderCheck> | null>(
    null,
  );
  const [checkError, setCheckError] = useState<string | null>(null);
  // Rows with possible matches default to "same person - skip".
  const [addAsNewRowNumbers, setAddAsNewRowNumbers] = useState<Set<number>>(
    new Set(),
  );
  const { data: professionalTypes, isLoading: typesAreLoading } = useQuery({
    ...trpc.healthcareProfessionalTypes.getAll.queryOptions(),
    enabled: open,
  });

  const reset = () => {
    setFile(null);
    setRows([]);
    setSelectedRowNumbers(new Set());
    setFileError(null);
    setIsParsing(false);
    setChecks(null);
    setCheckError(null);
    setAddAsNewRowNumbers(new Set());
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  const checkProviders = useMutation(
    trpc.organizationBusinessUnitAffiliation.checkRosterProviders.mutationOptions(),
  );

  // Re-running the check resets the "add as new" choices: they approved the
  // previous matches, and a refreshed set may contain providers they never saw.
  const runCheck = async (parsedRows: ParsedProviderCsvRow[]) => {
    setCheckError(null);
    setChecks(null);
    setAddAsNewRowNumbers(new Set());
    // Invalid rows are never imported and may fail the check's validation.
    const checkableRows = parsedRows.filter((row) => row.errors.length === 0);
    if (checkableRows.length === 0) {
      setChecks(new Map());
      return;
    }
    try {
      const results = await checkProviders.mutateAsync({
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        providers: checkableRows.map((row) => ({
          email: row.values.email,
          npi: row.values.npi,
          firstName: row.values.firstName,
          lastName: row.values.lastName,
          sendInvitation: row.values.sendInvitation,
        })),
      });
      setChecks(
        new Map(
          checkableRows.map((row, index) => [row.rowNumber, results[index]]),
        ),
      );
    } catch (error) {
      setCheckError(
        error instanceof Error && error.message
          ? error.message
          : "Failed to check for existing providers.",
      );
    }
  };

  const createProviders = useMutation(
    trpc.organizationBusinessUnitAffiliation.createRosterProviders.mutationOptions(
      {
        onSuccess: async (results, variables) => {
          await onSaved?.(variables.organizationBusinessUnitId);
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey:
                trpc.organizationBusinessUnitAffiliation.listInvitations.queryKey(
                  {
                    organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                  },
                ),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.organizationBusinessUnitAffiliation.listForBusinessUnit.queryKey(
                  {
                    organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                  },
                ),
            }),
          ]);
          const invitationsSent = results.filter(
            (result) => result.invitationSent,
          ).length;
          toast.success(
            getImportSuccessMessage({
              providerCount: results.length,
              invitationCount: invitationsSent,
            }),
          );
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to import providers.");
          // Someone changed the roster since the preview; refresh the flags.
          if (
            parseRosterIdentityConflict(error) ||
            isRosterNameMatchError(error)
          ) {
            void runCheck(rows);
          }
        },
      },
    ),
  );

  const validRows = useMemo(
    () => rows.filter((row) => row.errors.length === 0),
    [rows],
  );
  // Rows already on the roster are excluded outright.
  const importableRows = validRows.filter(
    (row) => !checks?.get(row.rowNumber)?.identityConflict,
  );
  const selectedRows = importableRows.filter((row) =>
    selectedRowNumbers.has(row.rowNumber),
  );
  // A row with possible matches imports only once "Add as new provider" is chosen.
  const rowsToImport = selectedRows.filter(
    (row) =>
      !checks?.get(row.rowNumber)?.nameMatches.length ||
      addAsNewRowNumbers.has(row.rowNumber),
  );
  const allValidSelected =
    importableRows.length > 0 && selectedRows.length === importableRows.length;
  const someValidSelected = selectedRows.length > 0 && !allValidSelected;

  const selectFile = async (nextFile: File | null) => {
    setFileError(null);
    setFile(nextFile);
    setRows([]);
    setChecks(null);
    setCheckError(null);
    setSelectedRowNumbers(new Set());
    if (!nextFile) {
      return;
    }

    setIsParsing(true);
    try {
      const parsedRows = parseProviderCsv({
        text: await nextFile.text(),
        professionalTypes: professionalTypes ?? [],
      });
      setRows(parsedRows);
      void runCheck(parsedRows);
      setSelectedRowNumbers(
        new Set(
          parsedRows
            .filter((row) => row.errors.length === 0)
            .map((row) => row.rowNumber),
        ),
      );
    } catch (error) {
      const message =
        error instanceof ProviderCsvError
          ? error.message
          : "The CSV could not be read.";
      setFileError(message);
    } finally {
      setIsParsing(false);
    }
  };

  const toggleRow = (rowNumber: number, selected: boolean) => {
    setSelectedRowNumbers((current) => {
      const next = new Set(current);
      if (selected) {
        next.add(rowNumber);
      } else {
        next.delete(rowNumber);
      }
      return next;
    });
  };

  const toggleAll = (selected: boolean) => {
    setSelectedRowNumbers(
      selected
        ? new Set(importableRows.map((row) => row.rowNumber))
        : new Set(),
    );
  };

  const toggleAddAsNew = (rowNumber: number, addAsNew: boolean) => {
    setAddAsNewRowNumbers((current) => {
      const next = new Set(current);
      if (addAsNew) {
        next.add(rowNumber);
      } else {
        next.delete(rowNumber);
      }
      return next;
    });
  };

  const submit = () => {
    createProviders.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      providers: rowsToImport.map((row) => ({
        email: row.values.email,
        firstName: row.values.firstName,
        lastName: row.values.lastName,
        phone: row.values.phone,
        npi: row.values.npi,
        healthcareProfessionalTypeId: row.values.healthcareProfessionalTypeId,
        specialtyCodes: row.values.specialtyCodes,
        sendInvitation: row.values.sendInvitation,
        reviewedNameMatchIds: checks
          ?.get(row.rowNumber)
          ?.nameMatches.map((match) => match.id),
      })),
    });
  };

  const hasPreview = rows.length > 0;
  const invalidCount = rows.length - validRows.length;
  const skippedCount = rows.length - rowsToImport.length;
  const isChecking = checkProviders.isPending || (hasPreview && !checks);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!createProviders.isPending) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Import providers from CSV</DialogTitle>
          <DialogDescription>
            The CSV is parsed in your browser. Review and select the providers
            below before anything is sent to Saile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 p-3">
            <div>
              <p className="text-sm font-medium">Need a starting point?</p>
              <p className="text-xs text-muted-foreground">
                Professional type must match a Saile option. Specialty codes use
                semicolons; send invitation accepts true or false.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={downloadProviderCsvTemplate}
            >
              <Download className="h-4 w-4" />
              Download example CSV
            </Button>
          </div>

          <CsvFileDropzone
            file={file}
            disabled={typesAreLoading || isParsing || createProviders.isPending}
            onFile={selectFile}
            onError={setFileError}
          />

          {(typesAreLoading || isParsing) && (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {typesAreLoading ? "Loading professional types…" : "Reading CSV…"}
            </div>
          )}

          {hasPreview && checkProviders.isPending && (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking for existing providers…
            </div>
          )}

          {checkError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-3">
                {checkError}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void runCheck(rows)}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {fileError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{fileError}</AlertDescription>
            </Alert>
          )}

          {hasPreview && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span>
                    {rowsToImport.length} will be imported, {skippedCount}{" "}
                    skipped
                  </span>
                </div>
                {invalidCount > 0 && (
                  <span className="text-sm text-destructive">
                    {invalidCount} invalid row
                    {invalidCount === 1 ? "" : "s"} cannot be selected
                  </span>
                )}
              </div>

              <div className="max-h-[45vh] overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          aria-label="Select all valid providers"
                          checked={getSelectAllState({
                            allSelected: allValidSelected,
                            someSelected: someValidSelected,
                          })}
                          onCheckedChange={(checked) =>
                            toggleAll(checked === true)
                          }
                        />
                      </TableHead>
                      <TableHead>Row</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>NPI</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Professional type</TableHead>
                      <TableHead>Specialties</TableHead>
                      <TableHead>Invite</TableHead>
                      <TableHead>Issues</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => {
                      const check = checks?.get(row.rowNumber);
                      const isAlreadyOnRoster = Boolean(
                        check?.identityConflict,
                      );
                      const isValid = row.errors.length === 0;
                      const isSelectable = isValid && !isAlreadyOnRoster;
                      return (
                        <TableRow
                          key={row.rowNumber}
                          className={
                            !isSelectable ? "bg-destructive/5" : undefined
                          }
                        >
                          <TableCell>
                            <Checkbox
                              aria-label={`Select CSV row ${row.rowNumber}`}
                              disabled={!isSelectable}
                              checked={selectedRowNumbers.has(row.rowNumber)}
                              onCheckedChange={(checked) =>
                                toggleRow(row.rowNumber, checked === true)
                              }
                            />
                          </TableCell>
                          <TableCell>{row.rowNumber}</TableCell>
                          <TableCell className="min-w-32">
                            {displayName(row)}
                          </TableCell>
                          <TableCell className="min-w-48">
                            {row.values.email ?? "—"}
                          </TableCell>
                          <TableCell>{row.values.npi ?? "—"}</TableCell>
                          <TableCell>{row.values.phone ?? "—"}</TableCell>
                          <TableCell>
                            {row.values.professionalTypeName ?? "—"}
                          </TableCell>
                          <TableCell>
                            {row.values.specialtyCodes?.join(", ") || "—"}
                          </TableCell>
                          <TableCell>
                            {row.values.sendInvitation ? "Yes" : "No"}
                          </TableCell>
                          <TableCell className="min-w-56 text-xs">
                            {!isValid && (
                              <span className="text-destructive">
                                {row.errors.join("; ")}
                              </span>
                            )}
                            {isValid && check?.identityConflict && (
                              <span className="text-destructive">
                                {IDENTITY_CONFLICT_LABEL}:{" "}
                                {check.identityConflict.message}
                              </span>
                            )}
                            {isValid &&
                              !check?.identityConflict &&
                              check &&
                              check.nameMatches.length > 0 && (
                                <div className="space-y-1">
                                  <p className="text-amber-700">
                                    Possible match: {describeMatches(check)}
                                  </p>
                                  <label className="flex items-center gap-1.5">
                                    <Checkbox
                                      aria-label={`Add CSV row ${row.rowNumber} as new provider`}
                                      checked={addAsNewRowNumbers.has(
                                        row.rowNumber,
                                      )}
                                      onCheckedChange={(checked) =>
                                        toggleAddAsNew(
                                          row.rowNumber,
                                          checked === true,
                                        )
                                      }
                                    />
                                    {addAsNewRowNumbers.has(row.rowNumber)
                                      ? "Add as new provider"
                                      : "Same person — skip"}
                                  </label>
                                </div>
                              )}
                            {isValid &&
                              check &&
                              !check.identityConflict &&
                              check.nameMatches.length === 0 && (
                                <span className="text-emerald-700">Ready</span>
                              )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4 sm:space-x-0">
          <Button
            type="button"
            variant="outline"
            disabled={createProviders.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="gap-2"
            disabled={
              rowsToImport.length === 0 ||
              isChecking ||
              Boolean(checkError) ||
              createProviders.isPending
            }
            onClick={submit}
          >
            {createProviders.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Import {rowsToImport.length || "selected"} provider
            {rowsToImport.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
